import type { HalftoneParameters } from "../../core/effects/halftone";

const offscreen = document.createElement("canvas");
const offscreenContext = offscreen.getContext("2d", { willReadFrequently: true });

export function renderHalftone(
  target: CanvasRenderingContext2D,
  source: CanvasImageSource,
  width: number,
  height: number,
  parameters: HalftoneParameters,
) {
  if (!offscreenContext) return;

  if (offscreen.width !== width || offscreen.height !== height) {
    offscreen.width = width;
    offscreen.height = height;
  }

  offscreenContext.clearRect(0, 0, width, height);
  offscreenContext.drawImage(source, 0, 0, width, height);
  const pixels = offscreenContext.getImageData(0, 0, width, height).data;

  target.save();
  target.clearRect(0, 0, width, height);
  target.fillStyle = parameters.background;
  target.fillRect(0, 0, width, height);
  target.fillStyle = parameters.foreground;

  const cell = Math.max(2, Math.round(parameters.cellSize));
  const radians = (parameters.angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const centerX = width / 2;
  const centerY = height / 2;

  for (let y = cell / 2; y < height; y += cell) {
    for (let x = cell / 2; x < width; x += cell) {
      const sx = Math.max(0, Math.min(width - 1, Math.round(x)));
      const sy = Math.max(0, Math.min(height - 1, Math.round(y)));
      const index = (sy * width + sx) * 4;
      const r = pixels[index] ?? 0;
      const g = pixels[index + 1] ?? 0;
      const b = pixels[index + 2] ?? 0;
      let luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      luminance = Math.max(0, Math.min(1, luminance + parameters.threshold));
      if (!parameters.invert) luminance = 1 - luminance;

      const radius = Math.max(0, Math.min(cell * 0.75, luminance * (cell / 2) * parameters.dotGain));
      if (radius <= 0.05) continue;

      const dx = x - centerX;
      const dy = y - centerY;
      const rx = centerX + dx * cos - dy * sin;
      const ry = centerY + dx * sin + dy * cos;

      target.beginPath();
      target.arc(rx, ry, radius, 0, Math.PI * 2);
      target.fill();
    }
  }

  target.restore();
}
